import React, { useEffect, useState } from 'react';
import Modal from './ui/Modal';
import { toast } from 'react-hot-toast';
import { Article, Schedule, User } from '../types';
import { ScheduleService } from '../services/scheduleService';
import { ArticleService } from '../services/articleService';
import { formatScheduledAtGerman } from '../utils/dateFormatter';
import { createGermanDateTimeFromParts, getGermanDateTime, isWithinScheduleTolerance, parseScheduledAt } from '../utils/dateTimeUtils';
import { Button } from './ui/Button';
import { LoadingSpinner } from './ui/LoadingSpinner';
import {
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface ScheduleConfirmModalProps {
  authorId: string;
  platformId: string;
  currentUser: User;
  initialScheduledAt?: string | Date | null;
  onClose: () => void;
  onConfirm: (date: Date) => void;
  title?: string;
  confirmLabel?: string;
}

interface TimeSlot {
  date: Date;
  formatted: string;
  hasConflict: boolean;
  conflictingArticle?: Article;
}

export const ScheduleConfirmModal: React.FC<ScheduleConfirmModalProps> = ({
  authorId,
  platformId,
  currentUser,
  initialScheduledAt,
  onClose,
  onConfirm,
  title = 'Veröffentlichungstermin bestätigen',
  confirmLabel = 'Termin bestätigen'
}) => {
  const [authorSchedule, setAuthorSchedule] = useState<Schedule | null>(null);
  const [isFallbackSchedule, setIsFallbackSchedule] = useState(false);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadScheduleAndSlots = async () => {
      setIsLoading(true);
      try {
        const authorSchedules = await ScheduleService.getSchedulesByAuthor(authorId);
        let platformSchedule = authorSchedules.find(
          s => s.platformId === platformId && s.isActive
        );

        if (!platformSchedule) {
          platformSchedule = {
            id: 'fallback',
            platformId,
            authorId,
            dayOfWeek: 0,
            time: '10:00',
            isActive: true,
            createdAt: new Date(),
            updatedAt: new Date()
          };
          setIsFallbackSchedule(true);
        }

        setAuthorSchedule(platformSchedule);

        const slots = await calculateNextSlots(platformSchedule, 6);
        const slotsWithConflicts: TimeSlot[] = await Promise.all(
          slots.map(async (date) => {
            const conflictingArticle = await ArticleService.findConflictingArticle(
              authorId,
              platformId,
              date,
              currentUser
            );

            return {
              date,
              formatted: formatScheduledAtGerman(date.toISOString()),
              hasConflict: conflictingArticle !== null,
              conflictingArticle: conflictingArticle || undefined
            };
          })
        );

        setAvailableSlots(slotsWithConflicts);
        setSelectedSlot(slotsWithConflicts[0]?.date || null);
      } catch (error) {
        console.error('Fehler beim Laden der Termine:', error);
        toast.error('Fehler beim Laden der verfügbaren Termine');
        onClose();
      } finally {
        setIsLoading(false);
      }
    };

    loadScheduleAndSlots();
  }, [authorId, platformId, currentUser, onClose]);

  const calculateNextSlots = async (schedule: Schedule, count: number): Promise<Date[]> => {
    const slots: Date[] = [];
    const germanNow = getGermanDateTime();

    let targetDayOfWeekJS;
    if (schedule.dayOfWeek === 6) {
      targetDayOfWeekJS = 0;
    } else {
      targetDayOfWeekJS = schedule.dayOfWeek + 1;
    }

    const [scheduleHours, scheduleMinutes] = schedule.time.split(':').map(Number);
    let foundFirst = false;

    for (let i = 0; i < 365 && slots.length < count; i++) {
      const testDate = new Date(germanNow);
      testDate.setDate(germanNow.getDate() + i);

      if (testDate.getDay() === targetDayOfWeekJS) {
        const germanDateTime = createGermanDateTimeFromParts({
          year: testDate.getFullYear(),
          month: testDate.getMonth() + 1,
          day: testDate.getDate(),
          hours: scheduleHours,
          minutes: scheduleMinutes
        });

        if (!foundFirst) {
          if (germanDateTime > germanNow) {
            slots.push(germanDateTime);
            foundFirst = true;
          }
        } else {
          slots.push(germanDateTime);
        }
      }
    }

    return slots;
  };

  const handleConfirm = () => {
    if (!selectedSlot) {
      toast.error('Bitte wähle einen Termin aus');
      return;
    }
    onConfirm(selectedSlot);
  };

  const initialDate = parseScheduledAt(initialScheduledAt || null);

  return (
    <Modal title={title} onClose={onClose}>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner size="lg" />
              </div>
            ) : (
              <div className="space-y-4">
                {initialDate && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm font-medium text-blue-700">Aktueller Entwurfstermin:</p>
                    <p className="text-sm text-blue-900">{formatScheduledAtGerman(initialDate.toISOString())}</p>
                  </div>
                )}

                {authorSchedule && (
                  <p className="text-xs text-gray-500">
                    Regelmäßiger Termin: {ScheduleService.getDayName(authorSchedule.dayOfWeek)} um {authorSchedule.time} Uhr
                    {isFallbackSchedule ? ' (Standardtermin)' : ''}
                  </p>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Neuen Termin wählen:
                  </label>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {availableSlots.map((slot, index) => {
                      const isInitialSlot = initialDate
                        ? isWithinScheduleTolerance(initialDate, slot.date)
                        : false;

                      return (
                        <div
                          key={index}
                          className={`relative border rounded-lg p-3 cursor-pointer transition-colors ${
                            selectedSlot?.getTime() === slot.date.getTime()
                              ? 'border-blue-500 bg-blue-50'
                              : slot.hasConflict
                              ? 'border-yellow-300 bg-yellow-50 hover:bg-yellow-100'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                          onClick={() => setSelectedSlot(slot.date)}
                        >
                          <div className="flex items-start">
                            <input
                              type="radio"
                              name="publication-slot"
                              aria-label={slot.formatted}
                              checked={selectedSlot?.getTime() === slot.date.getTime()}
                              onChange={() => setSelectedSlot(slot.date)}
                              className="mt-1 mr-3"
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">
                                {slot.formatted}
                                {isInitialSlot && (
                                  <span className="ml-2 text-xs text-blue-600">(Entwurf)</span>
                                )}
                              </p>

                              {slot.hasConflict && slot.conflictingArticle && (
                                <div className="flex items-start mt-1">
                                  <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600 mr-1 flex-shrink-0 mt-0.5" />
                                  <p className="text-xs text-yellow-700">
                                    Konflikt mit: "{slot.conflictingArticle.title || 'Ohne Titel'}"
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          <div className="action-row">
            <Button
              onClick={handleConfirm}
              disabled={isLoading || !selectedSlot}
              className="w-full sm:w-auto"
            >
              {confirmLabel}
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              className="w-full sm:w-auto mt-3 sm:mt-0"
            >
              Abbrechen
            </Button>
          </div>
    </Modal>
  );
};
